import React, { ChangeEvent, useEffect } from "react";
import {
  Box,
  Button,
  FormControl,
  Grid,
  InputLabel,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

import Loader from "../../components/loader/loader";
import TooltipWithClickAway from "../../components/terminalPicker/TooltipWithClickAway";
import { MisdeliveryBolFormValues } from "../../@hooks/useMisdeliveryBolForm";
import { useBolEmail } from "./useBolEmail";
import { formatBytes } from "./BOLEmail.utils";
import { EMAIL_VALIDATION } from "./BOLEmail.constants";

// ============================================================================
// Sub-Components
// ============================================================================

interface EmailFieldProps {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  required?: boolean;
  error?: string;
  showTooltip?: boolean;
}

const EmailField: React.FC<EmailFieldProps> = ({
  id,
  label,
  value,
  onChange,
  disabled = false,
  required = false,
  error = "",
  showTooltip = false,
}) => (
  <FormControl fullWidth size="small">
    <InputLabel
      style={{ fontSize: "13px" }}
      shrink
      className="label-email"
      htmlFor={id}
    >
      {label}
      {required && <sup>*</sup>}
      {showTooltip && (
        <>
          {" "}
          <TooltipWithClickAway
            tooltipText="Enter multiple email addresses, separated by commas."
            tooltipKey={`tooltip-${id}`}
            data-testid={`tooltip-${id}`}
          />
        </>
      )}
    </InputLabel>
    <TextField
      autoComplete="off"
      id={id}
      name={id}
      variant="outlined"
      disabled={disabled}
      value={value}
      onChange={(e: ChangeEvent<HTMLInputElement>) => onChange(e.target.value)}
      inputProps={{ maxLength: EMAIL_VALIDATION.MAX_LENGTH }}
      error={Boolean(error)}
      helperText={error}
    />
  </FormControl>
);

interface SubjectFieldProps {
  value: string;
}

const SubjectField: React.FC<SubjectFieldProps> = ({ value }) => (
  <FormControl fullWidth size="small">
    <InputLabel
      style={{ fontSize: "13px" }}
      className="m-20"
      shrink
      htmlFor="subject"
    >
      Subject<sup>*</sup>
    </InputLabel>
    <TextField
      autoComplete="off"
      id="subject"
      name="subject"
      variant="outlined"
      value={value}
      inputProps={{ maxLength: EMAIL_VALIDATION.SUBJECT_MAX_LENGTH }}
      disabled
    />
  </FormControl>
);

interface PdfPreviewProps {
  file: File | null;
  onDownload: () => void;
}

const PdfPreview: React.FC<PdfPreviewProps> = ({ file, onDownload }) => {
  const handleKeyDown = (e: React.KeyboardEvent): void => {
    if (e.key === "Enter" || e.key === " ") {
      onDownload();
    }
  };

  return (
    <Box // NOSONAR
      sx={{
        border: 1,
        borderColor: "error.main",
        borderRadius: 1,
        height: 48,
        px: 0.75,
        py: 0.25,
        display: "flex",
        alignItems: "center",
        gap: 0.75,
        overflow: "hidden",
        cursor: file ? "pointer" : "default",
        "&:hover": {
          bgcolor: file ? "action.hover" : "transparent",
        },
      }}
      role="button"
      tabIndex={file ? 0 : -1}
      aria-label={file ? `Download ${file.name}` : "No PDF available"}
      data-testid="pdf-preview-box"
      onClick={onDownload}
      onKeyDown={handleKeyDown}
    >
      <PictureAsPdfIcon color="error" fontSize="small" />
      <Stack spacing={0} sx={{ minWidth: 0 }}>
        <Typography variant="caption" noWrap sx={{ lineHeight: 1.1 }}>
          {file?.name ?? "No file selected"}
        </Typography>
        <Typography
          variant="caption"
          noWrap
          sx={{ lineHeight: 1.1, color: "text.secondary" }}
        >
          {file ? formatBytes(file.size) : ""}
        </Typography>
      </Stack>
      <Stack
        spacing={-0.5}
        sx={{ ml: "auto", alignItems: "center", color: "text.secondary" }}
      >
        <ArrowDropUpIcon fontSize="small" sx={{ fontSize: 24 }} />
        <ArrowDropDownIcon fontSize="small" sx={{ fontSize: 24 }} />
      </Stack>
    </Box>
  );
};

interface HeaderProps {
  onSend: () => void;
}

const Header: React.FC<HeaderProps> = ({ onSend }) => (
  <div className="header-section">
    <div className="back-section">
      <div className="page-header-content">
        <div className="page-header" style={{ marginLeft: "25px" }}>
          BOL Preview
        </div>
      </div>
    </div>
    <div className="edit-section" style={{ marginRight: "35px" }}>
      <Button
        variant="contained"
        data-testid="send-button"
        className="assign"
        onClick={onSend}
      >
        Send
      </Button>
    </div>
  </div>
);

// ============================================================================
// PDF Generation
// ============================================================================

const generatePdfFile = async (
  bol: MisdeliveryBolFormValues,
  setPdfFile: (file: File | null) => void,
  setLoading: (loading: boolean) => void
): Promise<void> => {
  setLoading(true);
  try {
    const [{ pdf }, { default: MisdeliveryBolPdf }] = await Promise.all([
      import("@react-pdf/renderer"),
      import("./pdf/MisdeliveryBolPdf"),
    ]);

    const logoSrc = `${globalThis.location.origin}/Saia_ltl.png`;
    const blob = await pdf(
      <MisdeliveryBolPdf values={bol} logoSrc={logoSrc} />
    ).toBlob();

    const pickup = (bol.pickupNumber ?? "").toString().trim();
    const filename = `Misdelivery_BOL_${pickup}.pdf`;
    setPdfFile(new File([blob], filename, { type: "application/pdf" }));
  } catch (error) {
    console.error("Error generating PDF:", error);
    setPdfFile(null);
  } finally {
    setLoading(false);
  }
};

// ============================================================================
// Main Component
// ============================================================================

const BOLEmail: React.FC = () => {
  const {
    loading,
    bol,
    pdfFile,
    emailState,
    isHazmatPending,
    setTo,
    setCc,
    setHtml,
    clearError,
    handleSend,
    handleDownloadPdf,
    setPdfFile,
    setLoading,
  } = useBolEmail();

  const { to, cc, subject, html, error } = emailState;

  // Generate PDF when BOL is available
  useEffect(() => {
    if (bol) {
      generatePdfFile(bol, setPdfFile, setLoading);
    }
  }, [bol, setPdfFile, setLoading]);

  const handleToChange = (value: string): void => {
    setTo(value);
    clearError();
  };

  return (
    <div className="bg-layout">
      <Header onSend={handleSend} />

      <div className="content-area osd-description">
        <Grid container spacing={2} className="filter-margin padding-15 mb-0">
          <Grid item md={6} xs={12}>
            <EmailField
              id="to"
              label="To"
              value={to}
              onChange={handleToChange}
              disabled={isHazmatPending}
              required
              error={error}
              showTooltip
            />
          </Grid>

          {!isHazmatPending && (
            <Grid item md={6} xs={12}>
              <EmailField
                id="cc"
                label="CC"
                value={cc}
                onChange={setCc}
                disabled
                required
              />
            </Grid>
          )}

          <Grid item md={isHazmatPending ? 6 : 12} xs={12}>
            <SubjectField value={subject} />
          </Grid>

          <Grid item md={3} xs={12}>
            <PdfPreview file={pdfFile} onDownload={handleDownloadPdf} />
          </Grid>
        </Grid>

        <ReactQuill
          className="padding-15 pt-0"
          theme="snow"
          value={html}
          data-testid="quill-editor"
          onChange={setHtml}
        />
      </div>

      <Loader pageLoader={loading} />
    </div>
  );
};

export default BOLEmail;
